<template>
  <div :class="rootClasses" data-testid="integration-card">
    <div class="flex items-center gap-3">
      <span :class="iconClasses">
        <BaseIcon :name="icon" :size="18" />
      </span>
      <span class="text-sm font-semibold">{{ name }}</span>
    </div>
    <BaseTag :tone="tone === 'inverse' ? 'default' : 'accent'" :label="protocol" />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { cva, type VariantProps } from 'class-variance-authority';
import BaseTag from '@/components/base/BaseTag.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import type { IconName } from '@/components/base/icons';

const rootVariants = cva('flex items-center justify-between gap-4 rounded-xl border px-4 py-3', {
  variants: {
    tone: {
      default: 'border-stroke bg-paper text-ink',
      inverse: 'border-white/15 bg-white/5 text-ink-inverse',
    },
  },
  defaultVariants: { tone: 'default' },
});

const iconVariants = cva('inline-flex h-9 w-9 items-center justify-center rounded-lg', {
  variants: {
    tone: {
      default: 'bg-accent-soft text-accent-strong',
      inverse: 'bg-white/10 text-ink-inverse',
    },
  },
  defaultVariants: { tone: 'default' },
});

export type IntegrationCardTone = NonNullable<VariantProps<typeof rootVariants>['tone']>;

interface Props {
  name: string;
  protocol: string;
  tone?: IntegrationCardTone;
  icon?: IconName;
}

const props = withDefaults(defineProps<Props>(), {
  tone: 'default',
  icon: 'external',
});

const rootClasses = computed(() => rootVariants({ tone: props.tone }));
const iconClasses = computed(() => iconVariants({ tone: props.tone }));
</script>
